import React, { useEffect, useRef, useState } from "react";
import Editor from "../../common/Editor";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import useToast from "../../util/useToast";
import useAppContext from "../../useAppContext";
import clsx from "clsx";
import styles from "./SitesStatusesEditor.module.scss";
import Form from "react-bootstrap/esm/Form";
import Button from "../../common/Button";
import { apiUpdateSitesStatuses } from "../../api/ApiParameters";
import { apiGetContext } from "../../api/ApiApp";
import ColorBox from "../../common/ColorBox";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";

const SitesStatusesEditor = ({show, hide, onSubmit}) => {
  const {showToastSuccess} = useToast();

  const {sitesStatuses} = useAppContext();

  const queryClient = useQueryClient();

  const inputRefs = useRef([]);

  const [formErrors, setFormErrors] = useState({});

  const [formStatuses, setFormStatuses] = useState([]);

  const editMutation = useMutation(apiUpdateSitesStatuses());

  useEffect(() => {
    if (show) {
      setFormErrors({});

      setFormStatuses(sitesStatuses
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(status => ({...status})));

      inputRefs.current = [];
    }
  }, [show, sitesStatuses]);

  const submit = () => {
    return editMutation.mutateAsync(formStatuses)
      .then(() => {
        return (onSubmit ? onSubmit() : Promise.resolve()).then(() => {
          showToastSuccess("Statuts des chantiers modifiés");
        });
      })
      .then(() => queryClient.invalidateQueries(apiGetContext().queryKey))
      .then(hide)
      .catch((err) => {
        if (err.response?.status === 400) {
          setFormErrors(err.response.data.errors);
        }
      });
  };

  const addStatus = () => {
    setFormStatuses(prev => ([...prev, {name: "", color: "#555555|#FFFFFF"}]));

    setTimeout(() => {
      inputRefs.current[formStatuses.length].focus();
    }, 10);
  };

  const removeStatus = index => () => {
    setFormStatuses(prev => [...prev.slice(0, index), ...prev.slice(index + 1)]);
  };

  const updateFormData = (field, context = null) => e => {
    setFormStatuses(prev => {
      switch (field) {
        case "name":
          prev[context].name = e.target.value;
          break;
        case "color":
          prev[context].color = e;
          break;
        case "siteActive":
          prev[context].siteActive = e.target.checked;
          break;
        case "defaut":
          prev.forEach(s => s.defaut = false);

          prev[context].defaut = true;
          break;
        default:
          prev[context] = e.target.value;
          break;
      }

      return [...prev];
    });
  };

  return <Editor
    show={show}
    title={"Statuts des chantiers"}
    onSubmit={submit}
    onCancel={hide}
    submitLabel="Sauvegarder"
    cancelLabel="Annuler"
  >
    {formStatuses.map((status, index) => {
      return <div key={status.id || index} className={clsx(styles.status)}>
        <Form.Group className="mb-2" controlId={"site-status-name-" + index}>
          <Row>
            <Form.Label column sm={3}>Nom</Form.Label>
            <Col sm={9} className={styles.name}>
              <div className={styles.input}>
                <Form.Control
                  ref={el => inputRefs.current[index] = el}
                  type="text"
                  value={status.name}
                  onChange={updateFormData("name", index)}
                  maxLength="100"
                />
              </div>

              <ColorBox value={status.color} onChange={updateFormData("color", index)} right/>

              <div className={styles.actions}>
                <span className="material-icons" onClick={removeStatus(index)}>delete</span>
              </div>
            </Col>
          </Row>
        </Form.Group>

        <Form.Group className="mb-2" controlId={"site-status-default-" + index}>
          <Row>
            <Form.Label column sm={3}></Form.Label>
            <Col sm={9}>
              <div className={styles.input}>
                <Form.Check
                  type="checkbox"
                  label="Statut par défaut"
                  checked={status.defaut}
                  onChange={updateFormData("defaut", index)}
                />
              </div>
            </Col>
          </Row>
        </Form.Group>

        <Form.Group className="mb-0" controlId={"site-status-site-active-" + index}>
          <Row>
            <Form.Label column sm={3}></Form.Label>
            <Col sm={9}>
              <div className={styles.input}>
                <Form.Check
                  type="checkbox"
                  label="Chantier actif"
                  checked={status.siteActive}
                  onChange={updateFormData("siteActive", index)}
                />
              </div>
            </Col>
          </Row>
        </Form.Group>
      </div>;
    })}

    <Row>
      <Col sm={3}></Col>
      <Col sm={9}>
        <Button variant="link" onClick={addStatus}>Ajouter</Button>
      </Col>
    </Row>
  </Editor>;
};

export default SitesStatusesEditor;